/*
  Muminity 2026 redesign — shared behaviour for the m2- sections.
  No dependencies. Every feature is opt-in through a data attribute, and each
  init is idempotent so sections re-rendered by the theme editor rebind.

    [data-m2-carousel]   scroll-snap track + prev/next arrows + progress bar
    [data-m2-vtile]      click-to-play video tile with a mute toggle
    [data-m2-menu]       mobile menu drawer
    [data-m2-add]        AJAX add-to-cart button (data-variant-id, data-qty)
    [data-m2-qty]        quantity stepper
*/
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ── Carousel ─────────────────────────────────────────────────── */
  function initCarousel(el) {
    if (el.dataset.m2Bound) return;
    el.dataset.m2Bound = '1';
    var track = el.querySelector('.m2-carousel__track');
    if (!track) return;
    var prev = el.querySelector('.m2-carousel__arrow--prev');
    var next = el.querySelector('.m2-carousel__arrow--next');
    var bar = el.querySelector('.m2-progress__bar');

    function step() {
      var item = track.firstElementChild;
      if (!item) return track.clientWidth;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    }
    function update() {
      var max = track.scrollWidth - track.clientWidth;
      var x = track.scrollLeft;
      if (prev) prev.disabled = x <= 2;
      if (next) next.disabled = x >= max - 2;
      if (bar) {
        var visible = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
        var w = Math.min(1, visible);
        bar.style.width = (w * 100) + '%';
        var ratio = max > 0 ? x / max : 0;
        // Pixel travel so a fixed-width thumb (40px on phones) also tracks correctly.
        var travel = bar.parentNode.clientWidth - bar.offsetWidth;
        bar.style.transform = 'translateX(' + (ratio * travel) + 'px)';
      }
    }
    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
    track.addEventListener('scroll', function () { window.requestAnimationFrame(update); }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ── Video tile ─────────────────────────────────────────────────
     Matches the live homepage's feature cards: muted autoplay while on
     screen (desktop), still image only on phones when one is set, and the
     card opens its product popup. */
  var PHONE = window.matchMedia ? window.matchMedia('(max-width: 820px)') : { matches: false };
  var autoObserver = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      var v = en.target, tile = v.closest('.m2-vtile');
      v.dataset.inview = en.isIntersecting ? '1' : '0';
      if (en.isIntersecting && !(tile && tile.dataset.userPaused)) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
      else if (!en.isIntersecting) v.pause();
    });
  }, { rootMargin: '200px' }) : null;

  function initVtile(el) {
    if (el.dataset.m2Bound) return;
    el.dataset.m2Bound = '1';
    var video = el.querySelector('video');
    if (!video) return;
    var play = el.querySelector('.m2-vtile__play');
    var mute = el.querySelector('.m2-vtile__mute');
    var stillOnly = PHONE.matches && el.classList.contains('m2-vtile--still');
    video.muted = true;
    function sync() { el.classList.toggle('is-playing', !video.paused); el.classList.toggle('is-unmuted', !video.muted); }
    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
    video.addEventListener('volumechange', sync);
    if (!stillOnly) {
      if (autoObserver) autoObserver.observe(video);
      else { var p = video.play(); if (p && p.catch) p.catch(function () {}); }
    }
    // Phone still cards: the icons behave like the card itself — open the
    // product popup, or follow the link (Instagram tiles).
    function asCard(e) {
      if (!stillOnly) return false;
      var target = el.getAttribute('data-m2-vmodal-open');
      if (target) { e.preventDefault(); e.stopPropagation(); openVmodal(target); }
      return true;
    }
    if (play) play.addEventListener('click', function (e) {
      if (asCard(e)) return;
      e.preventDefault(); e.stopPropagation();
      if (video.paused) { delete el.dataset.userPaused; video.play(); }
      else { el.dataset.userPaused = '1'; video.pause(); }
    });
    if (mute) mute.addEventListener('click', function (e) {
      if (asCard(e)) return;
      e.preventDefault(); e.stopPropagation();
      // Only one card makes sound at a time.
      if (video.muted) document.querySelectorAll('[data-m2-vtile] video').forEach(function (v) { if (v !== video) v.muted = true; });
      video.muted = !video.muted;
      if (video.paused) { delete el.dataset.userPaused; video.play(); }
    });
    sync();
  }
  document.addEventListener('visibilitychange', function () {
    document.querySelectorAll('[data-m2-vtile] video').forEach(function (v) {
      var tile = v.closest('.m2-vtile');
      if (document.hidden) v.pause();
      else if (v.dataset.inview === '1' && !(tile && tile.dataset.userPaused)) v.play().catch(function () {});
    });
  });

  /* ── Video product popup ──────────────────────────────────────────
     Desktop: dialog with video + product. Phones: full screen with sound,
     "Shop Now" slides the product panel up (is-revealed). */
  function resumeCards() {
    document.querySelectorAll('[data-m2-vtile] video').forEach(function (v) {
      var tile = v.closest('.m2-vtile');
      if (v.dataset.inview === '1' && !(tile && tile.dataset.userPaused)) v.play().catch(function () {});
    });
  }
  function openVmodal(id) {
    var m = document.getElementById(id);
    if (!m) return;
    if (m.parentNode !== document.body) document.body.appendChild(m);
    m.hidden = false;
    m.classList.remove('is-revealed');
    requestAnimationFrame(function () { m.classList.add('is-open'); });
    document.documentElement.classList.add('m2-vmodal-open');
    document.querySelectorAll('[data-m2-vtile] video').forEach(function (v) { v.pause(); });
    var vid = m.querySelector('video');
    if (vid) {
      // Opening the popup is the tap, so sound is allowed; fall back to muted.
      vid.muted = false;
      vid.onvolumechange = function () { m.classList.toggle('is-unmuted', !vid.muted); };
      var p = vid.play();
      if (p && p.catch) p.catch(function () { vid.muted = true; vid.play().catch(function () {}); });
      m.classList.toggle('is-unmuted', !vid.muted);
    }
    var c = m.querySelector('.m2-vmodal__close');
    if (c) c.focus({ preventScroll: true });
  }
  function closeVmodal(m) {
    if (!m) return;
    m.classList.remove('is-open', 'is-revealed');
    document.documentElement.classList.remove('m2-vmodal-open');
    var vid = m.querySelector('video');
    if (vid) vid.pause();
    setTimeout(function () { if (!m.classList.contains('is-open')) m.hidden = true; }, 260);
    resumeCards();
  }
  function selectPill(pill) {
    var modal = pill.closest('[data-m2-vmodal]');
    modal.querySelectorAll('[data-m2-vm-pill]').forEach(function (p) { p.classList.toggle('is-active', p === pill); });
    modal.querySelector('[data-m2-vm-id]').value = pill.dataset.id;
    modal.querySelector('[data-m2-vm-price]').textContent = pill.dataset.price;
    var cmp = modal.querySelector('[data-m2-vm-compare]');
    cmp.textContent = pill.dataset.compare || '';
    cmp.hidden = !pill.dataset.compare;
    var ok = pill.dataset.available !== 'false';
    var add = modal.querySelector('[data-m2-vm-add]');
    var buy = modal.querySelector('[data-m2-vm-buy]');
    add.disabled = !ok; buy.disabled = !ok;
    add.textContent = ok ? 'Add to cart' : 'Sold out';
    delete add.dataset.added;
  }
  function buyNow(btn) {
    var f = btn.closest('form'), lbl = btn.textContent;
    btn.disabled = true; btn.textContent = 'Redirecting…';
    addToCart([{ id: f.querySelector('[data-m2-vm-id]').value, quantity: 1 }])
      .then(function () { window.location.href = ROOT + 'checkout'; })
      .catch(function () { btn.disabled = false; btn.textContent = lbl; f.submit(); });
  }
  document.addEventListener('click', function (e) {
    var t = e.target;
    var opener = t.closest('[data-m2-vmodal-open]');
    if (opener && !t.closest('button')) { e.preventDefault(); openVmodal(opener.getAttribute('data-m2-vmodal-open')); return; }
    var closer = t.closest('[data-m2-vmodal-close]');
    if (closer) { closeVmodal(closer.closest('[data-m2-vmodal]')); return; }
    var reveal = t.closest('[data-m2-vmodal-reveal]');
    if (reveal) { reveal.closest('[data-m2-vmodal]').classList.add('is-revealed'); return; }
    var sound = t.closest('[data-m2-vmodal-sound]');
    if (sound) { var vm = sound.closest('[data-m2-vmodal]').querySelector('video'); if (vm) vm.muted = !vm.muted; return; }
    var pill = t.closest('[data-m2-vm-pill]');
    if (pill && !pill.disabled) { selectPill(pill); return; }
    var buy = t.closest('[data-m2-vm-buy]');
    if (buy && !buy.disabled) buyNow(buy);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { document.querySelectorAll('[data-m2-vmodal].is-open').forEach(closeVmodal); return; }
    if ((e.key === 'Enter' || e.key === ' ') && e.target.matches && e.target.matches('[data-m2-vmodal-open]')) {
      e.preventDefault();
      openVmodal(e.target.getAttribute('data-m2-vmodal-open'));
    }
  });
  document.addEventListener('submit', function (e) {
    var f = e.target.closest('[data-m2-vmodal] form');
    if (!f) return;
    e.preventDefault();
    var btn = f.querySelector('[data-m2-vm-add]');
    if (!btn || btn.disabled) return;
    if (btn.dataset.added === '1') { window.location.href = ROOT + 'cart'; return; }
    var lbl = btn.textContent;
    btn.disabled = true; btn.textContent = 'Adding…';
    addToCart([{ id: f.querySelector('[data-m2-vm-id]').value, quantity: 1 }])
      .then(function () { btn.disabled = false; btn.textContent = 'View cart'; btn.dataset.added = '1'; })
      .catch(function () { btn.disabled = false; btn.textContent = lbl; f.submit(); });
  });

  /* ── Mobile menu ──────────────────────────────────────────────── */
  function initMenu(el) {
    if (el.dataset.m2Bound) return;
    el.dataset.m2Bound = '1';
    var panel = document.getElementById(el.getAttribute('aria-controls'));
    if (!panel) return;
    function set(open) {
      el.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.classList.toggle('is-open', open);
      document.documentElement.classList.toggle('m2-menu-open', open);
    }
    el.addEventListener('click', function () { set(el.getAttribute('aria-expanded') !== 'true'); });
    panel.querySelectorAll('[data-m2-menu-close]').forEach(function (b) { b.addEventListener('click', function () { set(false); }); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') set(false); });
  }

  /* ── Quantity stepper ─────────────────────────────────────────── */
  function initQty(el) {
    if (el.dataset.m2Bound) return;
    el.dataset.m2Bound = '1';
    var input = el.querySelector('input');
    el.querySelectorAll('[data-m2-qty-step]').forEach(function (b) {
      b.addEventListener('click', function () {
        var v = (parseInt(input.value, 10) || 1) + parseInt(b.dataset.m2QtyStep, 10);
        input.value = Math.max(1, Math.min(99, v));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ── Cart ─────────────────────────────────────────────────────── */
  function setCount(n) {
    document.querySelectorAll('[data-cart-count]').forEach(function (b) { b.textContent = n; b.setAttribute('data-empty', n > 0 ? 'false' : 'true'); });
  }
  var toastTimer;
  function toast(msg) {
    var t = document.getElementById('m2-toast');
    if (!t) {
      t = document.createElement('div');
      t.id = 'm2-toast';
      t.className = 'm2-toast';
      t.setAttribute('role', 'status');
      t.innerHTML = '<span class="m2-toast__text"></span><a class="m2-btn m2-btn--light" href="' + ROOT + 'cart">View cart</a>';
      document.body.appendChild(t);
    }
    t.querySelector('.m2-toast__text').textContent = msg;
    t.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { t.classList.remove('is-visible'); }, 5000);
  }
  /*
    items: [{id, quantity}]. Resolves with the /cart.js object. On a network
    or validation error the caller falls back to a normal form post, so the
    customer is never left with nothing happening.
  */
  function addToCart(items) {
    return fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json().then(function (j) { if (!r.ok || j.status) throw new Error(j.description || 'Error'); return j; }); })
      .then(function () { return fetch(ROOT + 'cart.js').then(function (r) { return r.json(); }); })
      .then(function (cart) { setCount(cart.item_count); return cart; });
  }
  window.m2Cart = { add: addToCart, toast: toast, setCount: setCount };

  function initAdd(el) {
    if (el.dataset.m2Bound) return;
    el.dataset.m2Bound = '1';
    el.addEventListener('click', function (e) {
      e.preventDefault();
      if (el.disabled) return;
      var label = el.textContent;
      el.disabled = true;
      el.textContent = el.dataset.m2Adding || 'Adding…';
      addToCart([{ id: el.dataset.variantId, quantity: parseInt(el.dataset.qty || '1', 10) }])
        .then(function () { el.textContent = el.dataset.m2Added || 'Added'; toast((el.dataset.title || 'Item') + ' added to your cart'); setTimeout(function () { el.textContent = label; el.disabled = false; }, 1800); })
        .catch(function (err) { el.textContent = label; el.disabled = false; toast(err.message || 'Could not add to cart'); });
    });
  }

  /* ── Boot ─────────────────────────────────────────────────────── */
  function init(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-m2-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-m2-vtile]').forEach(initVtile);
    scope.querySelectorAll('[data-m2-menu]').forEach(initMenu);
    scope.querySelectorAll('[data-m2-qty]').forEach(initQty);
    scope.querySelectorAll('[data-m2-add]').forEach(initAdd);
  }
  window.m2Init = init;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
